// Web implementation for react-native-maps using Leaflet
// This stub provides a functional MapView component for web platforms
// using the Leaflet mapping library - a lightweight, open-source solution
//
// No API key required (uses OpenStreetMap tiles)
// Features: markers, user location, zoom, pan, responsive design
//
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';

// Import Leaflet CSS - handled by webpack css-loader
// @ts-ignore - CSS import for webpack
import 'leaflet/dist/leaflet.css';

export const PROVIDER_GOOGLE = 'google';
export const PROVIDER_DEFAULT = 'default';

// Custom marker icon to replace the default one
const createMarkerIcon = () => {
  return L.icon({
    iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCAzMiA0MSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB4PSIxIiB5PSIxIiB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHJ4PSI0IiBmaWxsPSIjNjc1MWNjIiBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iMiIvPjxjaXJjbGUgY3g9IjE2IiBjeT0iMTYiIHI9IjQiIGZpbGw9IiNmZmYiLz48cG9seWdvbiBwb2ludHM9IjE2LDQxIDAgMjUgMzIgMjUiIGZpbGw9IiM2NzUxY2MiLz48L3N2Zz4=',
    iconSize: [32, 41],
    iconAnchor: [16, 41],
    popupAnchor: [0, -41],
  });
};

interface MapViewRef {
  leafletMap?: L.Map;
  markers: Map<string, L.Marker>;
}

interface MapViewProps {
  style?: any;
  initialRegion?: {
    latitude: number;
    longitude: number;
    latitudeDelta?: number;
    longitudeDelta?: number;
  };
  provider?: string;
  showsMyLocationButton?: boolean;
  showsCompass?: boolean;
  toolbarEnabled?: boolean;
  showsBuildings?: boolean;
  showsIndoors?: boolean;
  showsUserLocation?: boolean;
  scrollEnabled?: boolean;
  zoomEnabled?: boolean;
  pitchEnabled?: boolean;
  rotateEnabled?: boolean;
  children?: React.ReactNode;
}

export const MapView = React.forwardRef<MapViewRef>((props: MapViewProps, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const [isLoaded, setIsLoaded] = useState(false);

  const {
    style = {},
    initialRegion,
    showsUserLocation = false,
    scrollEnabled = true,
    zoomEnabled = true,
    children,
  } = props;

  // Expose map methods through ref
  React.useImperativeHandle(ref, () => ({
    leafletMap: mapRef.current || undefined,
    markers: markersRef.current,
  }));

  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize map
    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current, {
        scrollWheelZoom: scrollEnabled,
        zoomControl: zoomEnabled,
        doubleClickZoom: zoomEnabled,
        touchZoom: zoomEnabled,
        dragging: scrollEnabled,
      });

      // Add tile layer (OpenStreetMap)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(mapRef.current);

      // Set initial region
      if (initialRegion) {
        mapRef.current.setView(
          [initialRegion.latitude, initialRegion.longitude],
          14 // default zoom level
        );
      }

      // Add user location marker if enabled
      if (showsUserLocation && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const { latitude, longitude } = position.coords;
          L.circleMarker([latitude, longitude], {
            radius: 8,
            fillColor: '#4285F4',
            color: '#fff',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8,
          }).addTo(mapRef.current!);
        });
      }

      setIsLoaded(true);
    }

    // Update region if changed
    if (initialRegion && mapRef.current) {
      mapRef.current.setView([initialRegion.latitude, initialRegion.longitude], 14);
    }
  }, [initialRegion, showsUserLocation, scrollEnabled, zoomEnabled]);

  // Handle markers from children
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    // Process marker children
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === Marker) {
        const { coordinate, children: markerChildren } = child.props;
        if (coordinate) {
          const markerId = `${coordinate.latitude}-${coordinate.longitude}`;

          // Remove existing marker if present
          if (markersRef.current.has(markerId)) {
            mapRef.current!.removeLayer(markersRef.current.get(markerId)!);
          }

          // Create and add new marker
          const marker = L.marker([coordinate.latitude, coordinate.longitude], {
            icon: createMarkerIcon(),
          }).addTo(mapRef.current!);

          // Add popup if children exist (custom marker content)
          if (markerChildren) {
            const popupContent = document.createElement('div');
            popupContent.style.padding = '8px';
            marker.bindPopup(popupContent);
          }

          markersRef.current.set(markerId, marker);
        }
      }
    });
  }, [children, isLoaded]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: 300,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: '#f0f0f0',
        ...style,
      }}
    />
  );
});

MapView.displayName = 'MapView';

interface MarkerProps {
  coordinate: {
    latitude: number;
    longitude: number;
  };
  children?: React.ReactNode;
}

export const Marker = React.forwardRef<any, MarkerProps>((props: MarkerProps) => {
  return null; // Marker logic is handled in MapView
});

Marker.displayName = 'Marker';

export const Callout = React.forwardRef<HTMLDivElement>((props: any, ref) => {
  return <div ref={ref} style={{ display: 'none' }} />;
});

Callout.displayName = 'Callout';

export const Polygon = React.forwardRef<any>((props: any, ref) => {
  return null;
});

Polygon.displayName = 'Polygon';

export const Polyline = React.forwardRef<any>((props: any, ref) => {
  return null;
});

Polyline.displayName = 'Polyline';

export const Circle = React.forwardRef<any>((props: any, ref) => {
  return null;
});

Circle.displayName = 'Circle';

export const Heatmap = React.forwardRef<any>((props: any, ref) => {
  return null;
});

Heatmap.displayName = 'Heatmap';

export default MapView;
